import { registerPlugin } from '@capacitor/core';

export interface HealthConnectPluginInterface {
  checkAvailability(): Promise<{
    available: boolean;
    sdkStatus: number;
    hasPermission: boolean;
    simulatedBpm?: number | null;
  }>;
  openHealthSettings(): Promise<{ success: boolean; fallback?: boolean }>;
  openFitbit(): Promise<{ success: boolean; alias?: string }>;
  requestPermissions(): Promise<{ success: boolean; action?: string }>;
  setSimulatedBpm(options: { bpm: number | null }): Promise<{ success: boolean; simulatedBpm: number | null }>;
  getLatestHeartRate(): Promise<{
    bpm: number | null;
    timestamp?: number;
    source?: string;
    needsPermission?: boolean;
    error?: string;
    active?: boolean;
    message?: string;
    ageMinutes?: number;
    ageFormatted?: string;
    isLive?: boolean;
    appOrigin?: string;
  }>;
}

export const HealthConnect = registerPlugin<HealthConnectPluginInterface>('HealthConnect');

export interface BiometricState {
  bpm: number | null;
  isAvailable: boolean;
  hasPermission: boolean;
  isSyncing: boolean;
  lastUpdated: number | null;
  mode: 'watch' | 'simulated' | 'idle';
  source?: string;
  ageFormatted?: string;
  isLive?: boolean;
}

class BioTelemetryService {
  private syncTimer: any = null;
  private currentBpm: number | null = null;
  private lastSource: string | null = null;
  private lastAgeFormatted: string | null = null;
  private isLive: boolean = false;
  private lastAppOrigin: string | null = null;
  private listeners: Array<(bpm: number | null) => void> = [];

  async init(): Promise<{ available: boolean; hasPermission: boolean }> {
    try {
      const res = await HealthConnect.checkAvailability();
      if (res.simulatedBpm) {
        this.currentBpm = res.simulatedBpm;
        this.lastSource = 'simulated_override';
        this.lastAgeFormatted = 'Simulated';
        this.isLive = true;
      }
      return { available: res.available, hasPermission: res.hasPermission };
    } catch {
      return { available: false, hasPermission: false };
    }
  }

  async requestPermissions(): Promise<boolean> {
    try {
      const res = await HealthConnect.requestPermissions();
      return Boolean(res.success);
    } catch {
      return false;
    }
  }

  async openSettings(): Promise<boolean> {
    try {
      const res = await HealthConnect.openHealthSettings();
      return Boolean(res.success);
    } catch {
      return false;
    }
  }

  async openFitbit(): Promise<boolean> {
    try {
      const res = await HealthConnect.openFitbit();
      return Boolean(res.success);
    } catch {
      return false;
    }
  }

  async setSimulatedHeartRate(bpm: number | null) {
    try {
      await HealthConnect.setSimulatedBpm({ bpm });
      this.currentBpm = bpm;
      this.lastSource = bpm ? 'simulated_override' : null;
      this.lastAgeFormatted = bpm ? 'Simulated' : null;
      this.isLive = Boolean(bpm);
      this.notify(bpm);
    } catch {}
  }

  async fetchHeartRate(): Promise<{
    bpm: number | null;
    source?: string;
    message?: string;
    ageFormatted?: string;
    isLive?: boolean;
    appOrigin?: string;
  }> {
    try {
      const res = await HealthConnect.getLatestHeartRate();
      if (res && res.bpm && res.bpm > 0) {
        this.currentBpm = res.bpm;
        this.lastSource = res.source || 'health_connect';
        this.lastAgeFormatted = res.ageFormatted || 'Live';
        this.isLive = Boolean(res.isLive);
        this.lastAppOrigin = res.appOrigin || 'com.fitbit.FitbitMobile';
        this.notify(res.bpm);
        return {
          bpm: res.bpm,
          source: res.source,
          message: res.message,
          ageFormatted: this.lastAgeFormatted,
          isLive: this.isLive,
          appOrigin: this.lastAppOrigin
        };
      }
      if (this.lastSource === 'simulated_override' && this.currentBpm) {
        return {
          bpm: this.currentBpm,
          source: 'simulated_override',
          ageFormatted: 'Simulated',
          isLive: true
        };
      }
      return {
        bpm: null,
        source: res?.source,
        message: res?.message || 'Awaiting fresh watch sync from Google Health'
      };
    } catch (e: any) {
      return { bpm: this.currentBpm, source: this.lastSource || undefined, message: e?.message };
    }
  }

  startSync(callback: (bpm: number | null) => void, intervalMs = 5000) {
    this.listeners.push(callback);
    if (!this.syncTimer) {
      this.fetchHeartRate();
      this.syncTimer = setInterval(() => {
        this.fetchHeartRate();
      }, intervalMs);
    }
  }

  stopSync(callback?: (bpm: number | null) => void) {
    if (callback) {
      this.listeners = this.listeners.filter((l) => l !== callback);
    } else {
      this.listeners = [];
    }

    if (this.listeners.length === 0 && this.syncTimer) {
      clearInterval(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private notify(bpm: number | null) {
    for (const l of this.listeners) {
      try {
        l(bpm);
      } catch {}
    }
  }

  getCurrentBpm(): number | null {
    return this.currentBpm;
  }

  getLastSource(): string | null {
    return this.lastSource;
  }

  getLastAgeFormatted(): string | null {
    return this.lastAgeFormatted;
  }

  getIsLive(): boolean {
    return this.isLive;
  }

  getLastAppOrigin(): string | null {
    return this.lastAppOrigin;
  }
}

export const bioTelemetry = new BioTelemetryService();
